import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "./csaApi";
import "./CsaCapture.css";

/**
 * Start a CSA audit.
 *
 * The client and the site come first, before any question is answered — the
 * report is for a site, and every later screen names it. Scheduling and
 * assigning a second auditor are a later step (§7d); a solo auditor starting
 * their own audit needs neither.
 *
 * Nominating a reviewer *is* here, because it is what makes the audit reachable
 * by the person who signs it off. The audit list shows a submitted audit to its
 * assigned reviewer; with nobody nominated, a Reviewer opens an empty list and
 * the review step can never happen. §7c is careful that this is an
 * **intention** and not a permission — the nominee may never look at it, and
 * someone else may review instead, which is why `assigned_reviewer_id` and
 * `reviewed_by` are separate columns.
 *
 * A client typed here that does not exist is created. That is deliberate: the
 * CSA client list starts empty, and blocking the first audit for a new client
 * on an admin would stop the module being usable on day one. Existing names are
 * matched case-insensitively, so nobody ends up with two spellings of the same
 * client splitting one site's history.
 */
export default function CsaNewAudit({ onCancel }) {
  const navigate = useNavigate();
  const back = onCancel || (() => navigate("/csa-audits"));

  const [orgs, setOrgs] = useState([]);
  const [orgName, setOrgName] = useState("");
  const [siteName, setSiteName] = useState("");
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [auditors, setAuditors] = useState([]);
  const [auditorId, setAuditorId] = useState("");
  const [clash, setClash] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [reviewerId, setReviewerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .listOrganizations()
      .then((r) => setOrgs(Array.isArray(r) ? r : []))
      .catch((e) => setError(e.message));
  }, []);

  /*
   * Never blocks the form. If the lookup fails — an older server without
   * /people, say — the audit is still bookable without a reviewer, which is
   * exactly what happened before this field existed.
   */
  useEffect(() => {
    api
      .listPeople("Auditor")
      .then((r) => setAuditors((r && r.people) || []))
      .catch(() => setAuditors([]));
  }, []);

  /*
   * Ask before saving, not after. A clash discovered once the audit exists is
   * a double-booking; this is a warning while it can still be avoided.
   * Debounced, because it fires on every date keystroke.
   */
  useEffect(() => {
    if (!auditorId || !start || !end || end < start) { setClash(null); return; }
    let live = true;
    const t = setTimeout(() => {
      api
        .scheduleConflicts(auditorId, start, end)
        .then((r) => live && setClash(r && r.conflicts && r.conflicts.length ? r : null))
        .catch(() => live && setClash(null));
    }, 400);
    return () => { live = false; clearTimeout(t); };
  }, [auditorId, start, end]);

  useEffect(() => {
    api
      .listPeople("Reviewer")
      .then((r) => setReviewers((r && r.people) || []))
      .catch(() => setReviewers([]));
  }, []);

  const known = orgs.find(
    (o) => o.org_name.toLowerCase() === orgName.trim().toLowerCase()
  );
  const isNewClient = orgName.trim() && !known;
  const siteIsNew =
    known && siteName.trim() &&
    !known.sites.some((s) => s.site_name.toLowerCase() === siteName.trim().toLowerCase());

  const ready = orgName.trim() && siteName.trim() && start && end && !busy;
  const datesWrong = start && end && end < start;

  const create = async () => {
    if (!ready || datesWrong) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.createAudit({
        organization: orgName.trim(),
        site_name: siteName.trim(),
        start_date: start,
        end_date: end,
        assigned_reviewer_id: reviewerId ? Number(reviewerId) : undefined,
        prepared_by: auditorId ? Number(auditorId) : undefined,
      });
      navigate(`/csa-audit/${r.audit_id}`);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="csa-capture csa-screen">
      <div className="cap-writeup" style={{ maxWidth: 560, paddingTop: 32 }}>
        <div className="wu-head">
          <div>
            <h2>New CSA audit</h2>
            <p>
              163 questions across six pillars. You can stop and come back at any
              point — everything saves as you go.
            </p>
          </div>
        </div>

        {error ? <p className="wu-error">{error}</p> : null}

        <div className="new-audit">
          <label htmlFor="na-org">Client</label>
          <input
            id="na-org"
            type="text"
            list="csa-orgs"
            value={orgName}
            placeholder="Start typing, or enter a new client"
            onChange={(e) => setOrgName(e.target.value)}
            autoComplete="off"
          />
          <datalist id="csa-orgs">
            {orgs.map((o) => (
              <option key={o.id} value={o.org_name} />
            ))}
          </datalist>
          {isNewClient ? (
            <p className="na-hint new">“{orgName.trim()}” is new — it will be added to the client list.</p>
          ) : null}

          <label htmlFor="na-site">Site</label>
          <input
            id="na-site"
            type="text"
            list="csa-sites"
            value={siteName}
            placeholder={known ? "Pick a site, or enter a new one" : "Site name"}
            onChange={(e) => setSiteName(e.target.value)}
            autoComplete="off"
          />
          <datalist id="csa-sites">
            {(known ? known.sites : []).map((s) => (
              <option key={s.id} value={s.site_name} />
            ))}
          </datalist>
          {siteIsNew ? (
            <p className="na-hint new">
              “{siteName.trim()}” is a new site for {known.org_name}.
            </p>
          ) : null}

          <div className="na-dates">
            <div>
              <label htmlFor="na-start">Start</label>
              <input
                id="na-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="na-end">End</label>
              <input
                id="na-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>
          {datesWrong ? <p className="na-hint bad">The end date is before the start date.</p> : null}

          {/*
            Optional, and said so. Review is never a gate (§7c) — the report can
            be issued from submitted — so an empty reviewer must not read like
            an unfinished form.
          */}
          {/*
            §7d: "Creating an audit includes a field, who it is for, defaulting
            to yourself." An auditor leaves it alone and has created their own
            work; a PM sets somebody else and has scheduled work for them.
          */}
          <label htmlFor="na-auditor">
            Who it is for <span className="na-optional">— defaults to you</span>
          </label>
          <select
            id="na-auditor"
            value={auditorId}
            onChange={(e) => setAuditorId(e.target.value)}
          >
            <option value="">Me</option>
            {auditors.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          {clash ? (
            <div className="na-clash">
              <strong>{clash.summary}</strong>
              <ul>
                {clash.conflicts.map((c) => (
                  <li key={c.audit_id}>
                    {c.organization} · {c.site} — {c.start_date} to {c.end_date}
                    {c.booked_by ? (
                      <span className="na-owner">booked by {c.booked_by}</span>
                    ) : c.owned_by ? (
                      <span className="na-owner">audit owned by {c.owned_by}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
              <p>
                You can still go ahead. Assigning them here takes them off those
                audits, which are then left with no auditor until their own
                project manager acts.
              </p>
            </div>
          ) : null}

          <label htmlFor="na-reviewer">
            Reviewer <span className="na-optional">— optional</span>
          </label>
          <select
            id="na-reviewer"
            value={reviewerId}
            onChange={(e) => setReviewerId(e.target.value)}
          >
            <option value="">Nobody — this audit can still be issued</option>
            {reviewers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {reviewerId ? (
            <p className="na-hint">
              This audit appears in their list once you submit it. They can sign
              it off or send it back — they cannot edit it.
            </p>
          ) : reviewers.length === 0 ? (
            <p className="na-hint">
              Nobody else holds the Reviewer role yet, so there is nobody to
              nominate. An Admin grants it.
            </p>
          ) : null}

          <div className="scope-actions">
            <button type="button" className="small-btn" onClick={back}>
              Cancel
            </button>
            <button
              type="button"
              className="cap-btn"
              disabled={!ready || !!datesWrong}
              onClick={create}
            >
              {busy ? "Creating…" : "Start the audit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
